import React, { useState, useEffect } from "react";
import { Flex, Typography, Button, Input, Table, Image, Card, Tabs, Upload, message, List, Popconfirm } from "antd";
import type { ColumnsType } from "antd/es/table";
import { InboxOutlined, ReloadOutlined, CopyOutlined, SearchOutlined, DeleteOutlined, FileImageOutlined } from "@ant-design/icons";
import type { UploadProps, UploadFile } from "antd";
import handleUploadFile, { handleBulkUploadFiles } from "../../../utils/uploadFileS3";
import { BASE_URL } from "../../../api/emptyV2Api";
import { useDeleteWebshopAssetMutation } from "../../../api/webshop/wsProductsApi";

const { Title, Text } = Typography;
const { Dragger } = Upload;
const dev_assets_url = "https://webshop-assets-20260531210740877800000001.s3.amazonaws.com/";
const prod_assets_url = "https://webshop-assets-20260531232150401200000002.s3.amazonaws.com/";
  
const S3_BUCKET_URL = (process.env.NODE_ENV === "production" || process.env.VERCEL_ENV === "production")
  ? prod_assets_url
  : dev_assets_url

export interface AssetFile {
  key: string;
  url?: string;
}

export default function WsAssetMgmtPage() {
  const [activeTab, setActiveTab] = useState("products/");
  const [files, setFiles] = useState<AssetFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [customFilename, setCustomFilename] = useState("");
  const [fileExtension, setFileExtension] = useState("");
  const [replacingKey, setReplacingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [deleteWebshopAsset] = useDeleteWebshopAssetMutation();

  const fetchFiles = async (prefix: string) => {
    setLoading(true);
    setError(null);
    try {
      const folder = prefix.replace("/", "");
      const response = await fetch(`${BASE_URL}webshop/products/manage?action=webshop-assets&folder=${folder}`);
      console.log("files", response)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setFiles(data);
    } catch (err: any) {
      console.error("Error listing files from S3 via API:", err);
      setError(err?.message || "Failed to fetch images from S3");
      // Provide fallback mock files in development mode so it's easy to test and review the UI layout
      if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") {
        setFiles([
          { key: `${prefix}thailand-daily-plan.png` },
          { key: `${prefix}singapore-unlimited.png` },
          { key: `${prefix}malaysia-travel-sim.png` },
          { key: `${prefix}indonesia-data-pack.png` },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles(activeTab);
  }, [activeTab]);

  const handleUpload = async () => {
    if (fileList.length === 0) return;
    if (fileList.length === 1 && !customFilename.trim()) {
      message.error("Please enter a name for the asset.");
      return;
    }
    setIsUploading(true);
    try {
      const folder = activeTab.replace("/", "");
      const uploadUrl = `${BASE_URL}webshop/products/manage?action=webshop-assets&folder=${folder}`;

      if (fileList.length === 1) {
        const fileToUpload = fileList[0] as unknown as File;
        const newName = fileExtension
          ? `${customFilename.trim()}.${fileExtension}`
          : customFilename.trim();

        const renamedFile = new File([fileToUpload], newName, { type: fileToUpload.type });
        const result = await handleUploadFile(uploadUrl, renamedFile);
        if (result) {
          message.success("Image uploaded successfully!");
          setFileList([]);
          setCustomFilename("");
          setFileExtension("");
          fetchFiles(activeTab);
        } else {
          message.error("Failed to upload image.");
        }
      } else {
        const filesToUpload = fileList.map(f => f as unknown as File);
        const { success, failed } = await handleBulkUploadFiles(uploadUrl, filesToUpload);
        
        if (success > 0) {
          message.success(`Successfully uploaded ${success} images!`);
        }
        if (failed > 0) {
          message.error(`Failed to upload ${failed} images.`);
        }
        
        if (success > 0) {
          setFileList([]);
          setCustomFilename("");
          setFileExtension("");
          fetchFiles(activeTab);
        }
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error("An error occurred during upload.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleReplaceImage = async (key: string, file: File) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp" || file.type === "image/gif";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG/WEBP/GIF files!");
      return;
    }
    setReplacingKey(key);
    try {
      const filename = key.substring(key.lastIndexOf("/") + 1);
      const folder = key.substring(0, key.lastIndexOf("/"));
      const renamedFile = new File([file], filename, { type: file.type });
      const uploadUrl = `${BASE_URL}webshop/products/manage?action=webshop-assets&folder=${folder}`;
      const result = await handleUploadFile(uploadUrl, renamedFile);
      if (result) {
        message.success("Image replaced successfully!");
        fetchFiles(activeTab);
      } else {
        message.error("Failed to replace image.");
      }
    } catch (err) {
      console.error("Replace error:", err);
      message.error("An error occurred during replacement.");
    } finally {
      setReplacingKey(null);
    }
  };

  const handleDeleteAsset = (record: AssetFile) => {
    setDeletingKey(record.key);
    return deleteWebshopAsset({ key: record.key })
      .unwrap()
      .then((res) => {
        message.success(res.message || `Deleted ${res.deleted?.[0] || record.key}`);
        setFiles((prev) => prev.filter((file) => file.key !== record.key));
        fetchFiles(activeTab);
      })
      .catch((err) => {
        console.error("API error:", err);
        message.error(
          err?.data?.message ||
            (typeof err?.data === "string" ? err.data : null) ||
            err?.error ||
            err?.message ||
            "Failed to delete asset",
        );
      })
      .finally(() => {
        setDeletingKey(null);
      });
  };

  const handleRemoveFile = (fileToRemove: UploadFile) => {
    setFileList((prev) => {
      const newList = prev.filter(f => f.uid !== fileToRemove.uid);
      if (newList.length === 0) {
        setCustomFilename("");
        setFileExtension("");
      } else if (newList.length === 1) {
        const remainingFile = newList[0];
        const lastDotIndex = remainingFile.name.lastIndexOf(".");
        let baseName = remainingFile.name;
        let ext = "";
        if (lastDotIndex !== -1) {
          baseName = remainingFile.name.substring(0, lastDotIndex);
          ext = remainingFile.name.substring(lastDotIndex + 1);
        }
        setCustomFilename(baseName);
        setFileExtension(ext);
      }
      return newList;
    });
  };

  const uploadProps: UploadProps = {
    name: "file",
    multiple: true,
    showUploadList: false,
    onRemove: (file) => {
      handleRemoveFile(file);
    },
    beforeUpload: (file, fileListBatch) => {
      const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp" || file.type === "image/gif";
      if (!isJpgOrPng) {
        message.error("You can only upload JPG/PNG/WEBP/GIF files!");
        return false;
      }
      
      setFileList((prev) => {
        const newList = [...prev, file];
        if (newList.length === 1) {
          const lastDotIndex = file.name.lastIndexOf(".");
          let baseName = file.name;
          let ext = "";
          if (lastDotIndex !== -1) {
            baseName = file.name.substring(0, lastDotIndex);
            ext = file.name.substring(lastDotIndex + 1);
          }
          setCustomFilename(baseName);
          setFileExtension(ext);
        }
        return newList;
      });
      return false; // prevent auto-upload
    },
    fileList,
  };

  const filteredFiles = files.filter((file) => {
    const filename = file.key.substring(file.key.lastIndexOf("/") + 1);
    return filename.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const columns: ColumnsType<AssetFile> = [
    {
      title: "Preview",
      key: "preview",
      width: 100,
      render: (_: any, record: AssetFile) => {
        const imgUrl = record.url || `${S3_BUCKET_URL}${record.key}`;
        return (
          <Image
            src={imgUrl}
            alt={record.key}
            width={80}
            height={80}
            style={{ objectFit: "cover", borderRadius: "6px", border: "1px solid #f0f0f0" }}
            fallback="https://placehold.co/80?text=Asset"
          />
        );
      },
    },
    {
      title: "File Name",
      key: "filename",
      render: (_: any, record: AssetFile) => {
        const filename = record.key.substring(record.key.lastIndexOf("/") + 1);
        return (
          <Flex vertical gap={4}>
            <Text strong style={{ fontSize: "14px" }}>{filename}</Text>
            <Text type="secondary" style={{ fontSize: "12px", fontFamily: "monospace" }}>{record.key}</Text>
          </Flex>
        );
      },
    },
    {
      title: "Full URL",
      key: "url",
      render: (_: any, record: AssetFile) => {
        const fullUrl = record.url || `${S3_BUCKET_URL}${record.key}`;
        return (
          <Text code copyable style={{ fontSize: "12px", wordBreak: "break-all" }}>
            {fullUrl}
          </Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 240,
      render: (_: any, record: AssetFile) => {
        const fullUrl = record.url || `${S3_BUCKET_URL}${record.key}`;
        return (
          <Flex gap={12} wrap="wrap">
            <Popconfirm
              title="Delete this asset from S3?"
              description="This does not unassign imageUrl on products."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeleteAsset(record)}
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                loading={deletingKey === record.key}
                style={{ padding: 0 }}
              >
                Delete
              </Button>
            </Popconfirm>
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(fullUrl);
                message.success("URL copied to clipboard!");
              }}
              style={{ padding: 0 }}
            >
              Copy URL
            </Button>
            <Upload
              beforeUpload={(file) => {
                handleReplaceImage(record.key, file);
                return false;
              }}
              showUploadList={false}
              accept="image/*"
            >
              <Button type="link" loading={replacingKey === record.key} style={{ padding: 0 }}>
                Replace
              </Button>
            </Upload>
          </Flex>
        );
      },
    },
  ];

  const tabItems = [
    {
      key: "products/",
      label: "Products",
    },
    {
      key: "network/",
      label: "Networks",
    },
  ];

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center">
        <div>
          <Title level={2} style={{ margin: 0 }}>Webshop Assets Management</Title>
          <Text type="secondary">Upload and manage product and vendor assets directly in the S3 bucket.</Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => fetchFiles(activeTab)}
          loading={loading}
        >
          Refresh Listing
        </Button>
      </Flex>

      {error && (
        <div style={{ color: "#cf1322", backgroundColor: "#fff1f0", border: "1px solid #ffa39e", padding: "8px 16px", borderRadius: "4px" }}>
          <Text type="danger">{error}. Using local development mock asset fallbacks for testing the interface.</Text>
        </div>
      )}

      <Card
        title="Upload Asset"
        bordered={false}
        style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.05)", borderRadius: "8px" }}
      >
        <Flex gap={16} align="stretch" wrap="wrap">
          <div style={{ width: "50%", minWidth: "280px" }}>
            <Dragger {...uploadProps} style={{ height: "100%" }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: "rgb(255, 70, 37)" }} />
              </p>
              <p className="ant-upload-text">Click or drag image file here to upload</p>
              <p className="ant-upload-hint">Supports JPG, PNG, WEBP, and GIF images</p>
            </Dragger>
          </div>
          <Flex vertical gap={8} style={{ flex: 1, minWidth: "280px", border: "1px solid #f0f0f0", borderRadius: "8px", padding: "16px", backgroundColor: "#fafafa" }}>
            {fileList.length === 0 && (
              <Flex vertical align="center" justify="center" style={{ height: "100%", color: "#bfbfbf", minHeight: "120px" }}>
                <FileImageOutlined style={{ fontSize: "32px", marginBottom: "8px" }} />
                <Text type="secondary">No files selected</Text>
              </Flex>
            )}
            {fileList.length === 1 && (
              <Flex vertical gap={12} style={{ height: "100%", justifyContent: "center" }}>
                <Flex vertical gap={4}>
                  <Text type="secondary">Selected File:</Text>
                  <Text strong>{fileList[0].name}</Text>
                </Flex>
                <Flex vertical gap={4}>
                  <Text strong>Rename Asset File <span style={{ color: "red" }}>*</span></Text>
                  <Input
                    addonAfter={fileExtension ? `.${fileExtension}` : undefined}
                    placeholder="Enter custom file name..."
                    value={customFilename}
                    onChange={(e) => setCustomFilename(e.target.value)}
                    disabled={isUploading}
                  />
                </Flex>
              </Flex>
            )}
            {fileList.length > 1 && (
              <Flex vertical gap={8} style={{ height: "100%", maxHeight: "200px" }}>
                <Text strong>Selected Files ({fileList.length})</Text>
                <div style={{ flex: 1, overflowY: "auto", paddingRight: "8px" }}>
                  <List
                    size="small"
                    dataSource={fileList}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveFile(item)}
                            disabled={isUploading}
                          />
                        ]}
                      >
                        <Text ellipsis style={{ maxWidth: '80%' }}>{item.name}</Text>
                      </List.Item>
                    )}
                  />
                </div>
                <Text type="secondary" style={{ fontSize: "12px", marginTop: "4px" }}>
                  Files will be uploaded with their original filenames.
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        <Flex gap={16} justify="flex-start" style={{ marginTop: "24px" }}>
          <Button
            type="primary"
            onClick={handleUpload}
            disabled={fileList.length === 0 || isUploading || (fileList.length === 1 && !customFilename.trim())}
            loading={isUploading}
            style={{
              backgroundColor: "rgb(255, 70, 37)",
              borderColor: "rgb(255, 70, 37)",
              minWidth: "120px",
            }}
          >
            Upload {fileList.length > 1 ? `${fileList.length} Files` : "Selected File"}
          </Button>
          {fileList.length > 0 && (
            <Button onClick={() => { setFileList([]); setCustomFilename(""); }} disabled={isUploading}>
              Clear All
            </Button>
          )}
        </Flex>
      </Card>

      <Card
        bordered={false}
        style={{
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          borderRadius: "8px",
        }}
        bodyStyle={{ padding: "0 16px 16px 16px" }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setSearchTerm("");
          }}
          items={tabItems}
        />

        <div style={{ marginTop: "16px", marginBottom: "16px" }}>
          <Input
            size="large"
            placeholder="Search assets by filename..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            allowClear
            style={{ width: "100%" }}
          />
        </div>

        <Table<AssetFile>
          rowKey={(record) => record.key}
          columns={columns}
          dataSource={filteredFiles}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </Flex>
  );
}
